// _admin/AdminAddCompetitorModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './AdminAddCompetitorModal.css';

const AdminAddCompetitorModal = ({ slug, onClose, onSave, isSaving }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [fields, setFields] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  // Estado para paquete/distancia/categoría
  const [paqueteSeleccionado, setPaqueteSeleccionado] = useState('');
  const [distanciaSeleccionada, setDistanciaSeleccionada] = useState('');
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');

  // ==========================================
  // CARGAR EL FORMCONFIG REAL DEL EVENTO
  // ==========================================
  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(`${baseUrl}/formConfigs/${slug}`);
        if (!response.ok) throw new Error('No se pudo cargar el formulario');
        const data = await response.json();
        const configFields = data.fields?.fields || data.fields || [];
        const configPackages = data.fields?.packages || data.packages || [];
        setFields(configFields);
        setPackages(configPackages);
      } catch (err) {
        console.error('[AdminCompetitorModal] Error:', err);
        setLoadError(err.message);
      } finally {
        setLoadingConfig(false);
      }
    };
    load();
  }, [slug, baseUrl]);

  // ==========================================
  // DETECTAR CAMPOS CLAVE POR LABEL
  // ==========================================
  const keyFields = useMemo(() => {
    const find = (regex) => fields.find(f => regex.test(f.label || ''));
    return {
      fecha: find(/fecha.*nacimiento|nacimiento/i),
      edad: find(/edad/i),
      categoria: fields.find(f => f.type === 'category'),
      distancia: find(/distancia|distance/i),
    };
  }, [fields]);

  // ==========================================
  // LÓGICA DE PAQUETE → DISTANCIA / CATEGORÍA
  // ==========================================

  // ¿El paquete seleccionado es VIRTUAL?
  const esPaqueteVirtual = useMemo(() => {
    if (!paqueteSeleccionado) return false;
    const pkg = packages.find(p => p.id === paqueteSeleccionado);
    return pkg?.title?.toUpperCase().includes('VIRTUAL') || false;
  }, [paqueteSeleccionado, packages]);

  // Opciones de distancia (solo para VIRTUAL)
  const opcionesDistancia = useMemo(() => {
    if (!keyFields.distancia?.options) return [];
    return keyFields.distancia.options.map(o =>
      typeof o === 'string' ? o : o.label
    ).filter(Boolean);
  }, [keyFields.distancia]);

  // Reglas de categoría que pertenecen al paquete seleccionado
  // 🟢 FIX: las reglas nuevas guardan varios paquetes en "packageIds" (array).
  // El campo viejo "packageId" (singular) solo se mantiene por compatibilidad,
  // así que hay que soportar ambos formatos o el filtro nunca matchea nada.
  const reglasDelPaquete = useMemo(() => {
    if (!keyFields.categoria?.categoryRules || !paqueteSeleccionado) return [];
    return keyFields.categoria.categoryRules.filter(rule => {
      const rulePackageIds =
        rule.packageIds && rule.packageIds.length > 0
          ? rule.packageIds.map(String)
          : rule.packageId
          ? [String(rule.packageId)]
          : [];
      return (
        rulePackageIds.length === 0 ||
        rulePackageIds.includes(String(paqueteSeleccionado))
      );
    });
  }, [keyFields.categoria, paqueteSeleccionado]);

  // Categorías disponibles según el paquete seleccionado (para el select manual)
  const categoriasDisponibles = useMemo(
    () => reglasDelPaquete.map(rule => rule.label).filter(Boolean),
    [reglasDelPaquete]
  );

  // 🟢 FIX: matchea una regla de categoría por edad, igual que useFormulario.
  // Si es "isSpecial" (Élite/Libre), aplica sin importar la edad.
  const matchCategoryRule = (rule, age) => {
    if (rule.isSpecial) return true;
    return age >= Number(rule.minAge || 0) && age <= Number(rule.maxAge || 99);
  };

  // ==========================================
  // HANDLER: CAMBIO DE PAQUETE
  // ==========================================
  const handlePaqueteChange = (paqueteId) => {
    setPaqueteSeleccionado(paqueteId);
    // Resetear distancia y categoría al cambiar paquete
    setDistanciaSeleccionada('');
    setCategoriaSeleccionada('');
  };

  // ==========================================
  // CALCULAR EDAD AUTOMÁTICAMENTE
  // ==========================================
  const calcularEdad = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) return '';
    const hoy = new Date();
    let edad = hoy.getFullYear() - fecha.getFullYear();
    const m = hoy.getMonth() - fecha.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < fecha.getDate())) edad--;
    return edad >= 0 ? String(edad) : '';
  };

  // ==========================================
  // 🟢 FIX: AUTO-ASIGNAR CATEGORÍA SEGÚN EDAD
  // ==========================================
  // Antes este modal solo listaba las reglas para que el admin elija
  // manualmente; nunca calculaba la categoría según la fecha de nacimiento
  // (ni respetaba el checkbox "Élite/Libre"). Ahora se auto-completa apenas
  // hay fecha de nacimiento + paquete válidos, igual que en el formulario
  // público. El select sigue editable por si el admin quiere sobreescribirla.
  useEffect(() => {
    if (esPaqueteVirtual || !paqueteSeleccionado || !keyFields.fecha) return;
    const fechaNacimiento = values[keyFields.fecha.id];
    if (!fechaNacimiento) return;

    const edad = parseInt(calcularEdad(fechaNacimiento), 10);
    if (isNaN(edad)) return;

    const reglaMatch = reglasDelPaquete.find(rule => matchCategoryRule(rule, edad));
    if (reglaMatch && reglaMatch.label !== categoriaSeleccionada) {
      setCategoriaSeleccionada(reglaMatch.label);
    }
  }, [values, keyFields.fecha, paqueteSeleccionado, esPaqueteVirtual, reglasDelPaquete]);

  // ==========================================
  // MANEJAR CAMBIO DE VALOR
  // ==========================================
  const handleChange = (fieldId, value) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
    setErrors(prev => ({ ...prev, [fieldId]: '' }));

    // Autocompletar edad si cambió la fecha de nacimiento
    if (keyFields.fecha && fieldId === keyFields.fecha.id) {
      const edad = calcularEdad(value);
      if (keyFields.edad && edad) {
        setValues(prev => ({ ...prev, [keyFields.edad.id]: edad }));
      }
    }
  };

  // ==========================================
  // VALIDAR CAMPOS REQUERIDOS
  // ==========================================
  const validar = () => {
    const nuevosErrores = {};

    // Validar campos del formulario
    fields.forEach(field => {
      // Saltar campos de distancia y categoría (se validan aparte)
      if (field.id === keyFields.distancia?.id) return;
      if (field.id === keyFields.categoria?.id) return;

      if (field.required && !values[field.id]) {
        nuevosErrores[field.id] = 'Este campo es obligatorio';
      }
    });

    // Validar paquete
    if (!paqueteSeleccionado) {
      nuevosErrores['paquete'] = 'Selecciona un paquete';
    }

    // Validar según tipo de paquete
    if (paqueteSeleccionado) {
      if (esPaqueteVirtual) {
        // VIRTUAL requiere distancia
        if (!distanciaSeleccionada) {
          nuevosErrores['distancia'] = 'Selecciona la distancia';
        }
      } else {
        // Otros paquetes requieren categoría
        if (!categoriaSeleccionada) {
          nuevosErrores['categoria'] = 'Selecciona una categoría';
        }
      }
    }

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // ==========================================
  // ENVIAR
  // ==========================================
  const handleSubmit = async () => {
    if (!validar()) return;

    // Construir array camposActivos
    const camposActivos = fields
      .filter(f => !f.isPackageInclude)
      .map(field => {
        let valor = values[field.id] || '';

        // Si es el campo distancia, usar el valor seleccionado
        if (field.id === keyFields.distancia?.id) {
          valor = esPaqueteVirtual ? distanciaSeleccionada : '';
        }
        // Si es el campo categoría, usar el valor seleccionado
        if (field.id === keyFields.categoria?.id) {
          valor = esPaqueteVirtual ? '' : categoriaSeleccionada;
        }

        return {
          nombre: field.label,
          valor: valor,
        };
      });

    // Agregar el paquete como campo extra
    const paqueteInfo = packages.find(p => p.id === paqueteSeleccionado);
    camposActivos.push({
      nombre: 'Paquete',
      valor: paqueteInfo?.title || '',
    });

    await onSave(camposActivos);
  };

  // ==========================================
  // RENDERIZAR CADA CAMPO SEGÚN SU TIPO
  // ==========================================
  const renderField = (field) => {
    if (field.isPackageInclude) return null;

    // Saltar campos de distancia y categoría (se renderizan aparte)
    if (field.id === keyFields.distancia?.id) return null;
    if (field.id === keyFields.categoria?.id) return null;

    const value = values[field.id] || '';
    const error = errors[field.id];
    const esEdad = keyFields.edad && field.id === keyFields.edad.id;

    // CAMPO EDAD: solo lectura (autocalculado)
    if (esEdad) {
      return (
        <div key={field.id} className="acm-field">
          <label className="acm-label">
            {field.label}
            {field.required && <span className="acm-req">*</span>}
          </label>
          <input
            type="text"
            value={value}
            readOnly
            disabled
            className="acm-input acm-readonly"
            placeholder="Se calcula automáticamente"
          />
        </div>
      );
    }

    // CAMPO FECHA DE NACIMIENTO
    if (keyFields.fecha && field.id === keyFields.fecha.id) {
      return (
        <div key={field.id} className="acm-field">
          <label className="acm-label">
            {field.label}
            {field.required && <span className="acm-req">*</span>}
          </label>
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={`acm-input ${error ? 'acm-error' : ''}`}
          />
          {error && <span className="acm-error-msg">{error}</span>}
        </div>
      );
    }

    // CAMPO SELECT GENÉRICO (ej: Genero)
    if (field.type === 'select') {
      const options = (field.options || []).map(o =>
        typeof o === 'string' ? o : o.label
      ).filter(Boolean);
      return (
        <div key={field.id} className="acm-field">
          <label className="acm-label">
            {field.label}
            {field.required && <span className="acm-req">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={`acm-input ${error ? 'acm-error' : ''}`}
          >
            <option value="">— Seleccionar —</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {error && <span className="acm-error-msg">{error}</span>}
        </div>
      );
    }

    // CAMPO EMAIL
    if (field.dataType === 'email') {
      return (
        <div key={field.id} className="acm-field">
          <label className="acm-label">
            {field.label}
            {field.required && <span className="acm-req">*</span>}
          </label>
          <input
            type="email"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={`acm-input ${error ? 'acm-error' : ''}`}
            placeholder={`Ingresa ${field.label.toLowerCase()}`}
          />
          {error && <span className="acm-error-msg">{error}</span>}
        </div>
      );
    }

    // CAMPO NÚMERO (integer)
    if (field.dataType === 'integer') {
      return (
        <div key={field.id} className="acm-field">
          <label className="acm-label">
            {field.label}
            {field.required && <span className="acm-req">*</span>}
          </label>
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={`acm-input ${error ? 'acm-error' : ''}`}
            placeholder={`Ingresa ${field.label.toLowerCase()}`}
          />
          {error && <span className="acm-error-msg">{error}</span>}
        </div>
      );
    }

    // CAMPO TEXTO GENÉRICO
    return (
      <div key={field.id} className="acm-field">
        <label className="acm-label">
          {field.label}
          {field.required && <span className="acm-req">*</span>}
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(field.id, e.target.value)}
          className={`acm-input ${error ? 'acm-error' : ''}`}
          placeholder={`Ingresa ${field.label.toLowerCase()}`}
        />
        {error && <span className="acm-error-msg">{error}</span>}
      </div>
    );
  };

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================
  return (
    <div className="acm-overlay" onClick={onClose}>
      <div className="acm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acm-header">
          <h3>➕ Agregar Nuevo Corredor</h3>
          <button className="acm-close" onClick={onClose}>✕</button>
        </div>

        {loadingConfig ? (
          <div className="acm-loading">⏳ Cargando formulario del evento...</div>
        ) : loadError ? (
          <div className="acm-error-box">❌ Error: {loadError}</div>
        ) : (
          <>
            <div className="acm-body">

              {/* ========================================== */}
              {/* CAMPO PAQUETE (siempre visible)            */}
              {/* ========================================== */}
              <div className="acm-field acm-field-paquete">
                <label className="acm-label">
                  📦 Paquete
                  <span className="acm-req">*</span>
                </label>
                <select
                  value={paqueteSeleccionado}
                  onChange={(e) => handlePaqueteChange(e.target.value)}
                  className={`acm-input ${errors['paquete'] ? 'acm-error' : ''}`}
                >
                  <option value="">— Seleccionar paquete —</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.title} — {pkg.description || 'Sin descripción'}
                    </option>
                  ))}
                </select>
                {errors['paquete'] && <span className="acm-error-msg">{errors['paquete']}</span>}
              </div>

              {/* ========================================== */}
              {/* CAMPO DISTANCIA (solo para VIRTUAL)        */}
              {/* ========================================== */}
              {esPaqueteVirtual && (
                <div className="acm-field acm-field-distancia">
                  <label className="acm-label">
                    🏃 Distancia que correrá
                    <span className="acm-req">*</span>
                  </label>
                  <select
                    value={distanciaSeleccionada}
                    onChange={(e) => setDistanciaSeleccionada(e.target.value)}
                    className={`acm-input ${errors['distancia'] ? 'acm-error' : ''}`}
                  >
                    <option value="">— Seleccionar distancia —</option>
                    {opcionesDistancia.map((dist) => (
                      <option key={dist} value={dist}>{dist}</option>
                    ))}
                  </select>
                  {errors['distancia'] && <span className="acm-error-msg">{errors['distancia']}</span>}
                  <small className="acm-hint">
                    ℹ️ El paquete VIRTUAL no tiene categorías, solo distancia.
                  </small>
                </div>
              )}

              {/* ========================================== */}
              {/* CAMPO CATEGORÍA (solo para NO-VIRTUAL)     */}
              {/* ========================================== */}
              {!esPaqueteVirtual && paqueteSeleccionado && (
                <div className="acm-field acm-field-categoria">
                  <label className="acm-label">
                    🏷️ Categoría
                    <span className="acm-req">*</span>
                  </label>
                  <select
                    value={categoriaSeleccionada}
                    onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                    className={`acm-input ${errors['categoria'] ? 'acm-error' : ''}`}
                  >
                    <option value="">— Seleccionar categoría —</option>
                    {categoriasDisponibles.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {errors['categoria'] && <span className="acm-error-msg">{errors['categoria']}</span>}
                </div>
              )}

              {/* ========================================== */}
              {/* RESTO DE CAMPOS DEL FORMULARIO             */}
              {/* ========================================== */}
              {fields.map(field => renderField(field))}
            </div>

            <div className="acm-footer">
              <button className="acm-btn-cancel" onClick={onClose} disabled={isSaving}>
                Cancelar
              </button>
              <button
                className="acm-btn-save"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Agregando...' : '💾 Agregar Corredor'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAddCompetitorModal;